/**
 * Typed errors for the V0.5.0 auth/error contract.
 *
 * Thrown by the API client interceptor (src/api/client.ts) so route-level
 * handlers can match on `instanceof` rather than parsing axios error
 * shapes. TanStack Query passes the thrown error through to the query's
 * `error` slot.
 *
 * Maps to the contract from the backend v0.5.0 handoff (§4):
 *
 *   401 + body.code === "TENSAW_BASE_ACCESS_TOKEN_EXPIRED"
 *     → interceptor calls getIdToken({ forceRefresh: true }), retries the
 *       request once. If the retry succeeds, no error is thrown. If the
 *       retry fails or refresh itself fails, a TerminalAuthError is thrown.
 *
 *   401 (anything else — INVALID, REAUTH_REQUIRED, etc.)
 *     → TerminalAuthError; the global handler signs the user out.
 *
 *   403 (authenticated but not authorized: missing role, or clinic_id
 *         outside the AM's scope)
 *     → AccessDeniedError. The route renders an access-denied UI; the user
 *       is NOT signed out (their session is fine, they just can't access
 *       this resource).
 *
 *   503 + body.code === "TENSAW_BASE_UPSTREAM_UNAVAILABLE"
 *     → UpstreamUnavailableError. TanStack Query's default retry handles
 *       transient JWKS/IdP blips; users aren't signed out on a Cognito
 *       outage.
 */

/**
 * 403 from the backend — the user's session is valid but their identity
 * lacks the permission for this resource. Two flavors:
 *
 *   - kind: "role"   — the user authenticated but doesn't have
 *                      RCM_ACCOUNT_MANAGER. They can't use the dashboard
 *                      at all.
 *   - kind: "clinic" — the user is an AM but the requested clinic isn't
 *                      in their scoped clinic_id set. They can use the
 *                      dashboard, just not for this particular clinic.
 *
 * The route-level handler decides what to render: a full-page access-
 * denied for "role" (no point in showing app chrome), an inline card with
 * a "Back to clinics" button for "clinic".
 */
export class AccessDeniedError extends Error {
  readonly kind: "role" | "clinic" | "unknown";
  readonly clinicId: number | null;
  readonly status: number;

  constructor(kind: AccessDeniedError["kind"], clinicId: number | null, message?: string) {
    super(message ?? `Access denied (${kind})`);
    this.name = "AccessDeniedError";
    this.kind = kind;
    this.clinicId = clinicId;
    this.status = 403;
  }
}

/**
 * Terminal 401 — the session can't be salvaged. The global handler
 * triggers signOut() and the Authenticator re-renders the login form.
 * Use sparingly: only thrown after refresh-and-retry has been attempted
 * and failed, or for non-EXPIRED 401 codes (INVALID, REAUTH_REQUIRED).
 */
export class TerminalAuthError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message?: string) {
    super(message ?? `Terminal authentication failure (${code})`);
    this.name = "TerminalAuthError";
    this.code = code;
    this.status = 401;
  }
}

/**
 * 503 — backend says the IdP/JWKS layer is having a transient blip. NOT
 * a logout trigger. TanStack Query retries 5xx by default; this typed
 * error lets the UI distinguish "we're degraded" from "you're locked
 * out" if it needs to.
 */
export class UpstreamUnavailableError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message?: string) {
    super(message ?? `Authentication infrastructure is temporarily unavailable (${code})`);
    this.name = "UpstreamUnavailableError";
    this.code = code;
    this.status = 503;
  }
}

/**
 * Type guards — TanStack Query's `error` slot is `unknown`, so consumers
 * narrow with these instead of `instanceof` checks (works across module
 * boundaries even with HMR).
 */
export function isAccessDenied(e: unknown): e is AccessDeniedError {
  return e instanceof Error && e.name === "AccessDeniedError";
}

export function isTerminalAuth(e: unknown): e is TerminalAuthError {
  return e instanceof Error && e.name === "TerminalAuthError";
}

export function isUpstreamUnavailable(e: unknown): e is UpstreamUnavailableError {
  return e instanceof Error && e.name === "UpstreamUnavailableError";
}
